import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useTeam } from '../context/TeamContext';

function ViewRotaPage() {
  const [rota, setRota] = useState(null);
  const [rotaName, setRotaName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedRota, setEditedRota] = useState(null);
  const [assignmentCounts, setAssignmentCounts] = useState({});
  const [showAssignmentCounts, setShowAssignmentCounts] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const { id } = useParams();
  const { teamMembers } = useTeam();

  const positions = ['Jimmy Jib', 'Selatan', 'Utara', 'Switcher', 'Title 1', 'Title 2', 'Animasi', 'Foto', 'Socmed'];
  const eventOrder = ['IBR1', 'IBR2', 'IBR3', 'IBR4', 'NOW', 'WBI', 'Wedding'];

  useEffect(() => {
    const fetchRota = async () => {
      const rotaRef = doc(db, 'rotas', id);
      const snapshot = await getDoc(rotaRef);
      if (snapshot.exists()) {
        const rotaData = snapshot.data();
        setRotaName(rotaData.name);
        setRota(rotaData.rota);
        setEditedRota(rotaData.rota);
        calculateAssignmentCounts(rotaData.rota);
      } else {
        console.error("Rota not found");
      }
    };

    fetchRota();
  }, [id]);

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const groupedAndSortedRota = useMemo(() => {
    if (!rota) return [];

    const entries = Object.entries(rota);
    
    const sortedEvents = entries.sort(([keyA], [keyB]) => {
      const [dateA, eventA] = keyA.split(' ');
      const [dateB, eventB] = keyB.split(' ');
      return dateA.localeCompare(dateB) || eventOrder.indexOf(eventA) - eventOrder.indexOf(eventB);
    });

    return sortedEvents.map(([key, assignments]) => {
      const [date, event] = key.split(' ');
      return { 
        date, 
        event, 
        assignments
      };
    });
  }, [rota, eventOrder]);

  const calculateAssignmentCounts = (rotaData) => {
    const counts = {};
    Object.values(rotaData).forEach(assignments => {
      Object.values(assignments).forEach(name => {
        if (name && name !== '-') {
          counts[name] = (counts[name] || 0) + 1;
        }
      });
    });
    setAssignmentCounts(counts);
  };

  const getQualifiedMembers = (position) => {
    const qualificationMap = {
      'Jimmy Jib': 'JimmyJib',
      'Selatan': 'Moving',
      'Utara': 'Moving',
      'Switcher': 'Switcher',
      'Title 1': 'Title',
      'Title 2': 'Title',
      'Animasi': 'Animasi',
      'Foto': 'Foto',
      'Socmed': 'Socmed'
    };
    const qualification = qualificationMap[position];
    return teamMembers.filter(member => member.qualifications[qualification]);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const checkForConflicts = () => {
    const conflictList = [];
    Object.entries(editedRota).forEach(([eventKey, assignments]) => {
      const assignedNames = new Set();
      const conflictingNames = new Set();

      Object.entries(assignments).forEach(([position, name]) => {
        if (name && name !== '-') {
          if (assignedNames.has(name)) {
            conflictingNames.add(name);
          } else {
            assignedNames.add(name);
          }
        }
      });

      if (conflictingNames.size > 0) {
        conflictList.push({
          event: eventKey,
          names: Array.from(conflictingNames)
        });
      }
    });

    setConflicts(conflictList);
    setShowConflicts(true);
    return conflictList.length === 0;
  };

  const handleSave = async () => {
    const noConflicts = checkForConflicts();
    if (!noConflicts) {
      alert('There are conflicts in the rota. Please resolve them before saving.');
      return;
    }

    try {
      const rotaRef = doc(db, 'rotas', id);
      await updateDoc(rotaRef, { rota: editedRota });
      setRota(editedRota);
      setIsEditing(false);
      calculateAssignmentCounts(editedRota);
      alert('Rota updated successfully!');
    } catch (error) {
      console.error('Error updating rota:', error);
      alert('Failed to update rota. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditedRota(rota);
    setIsEditing(false);
  };

  const handleAssignmentChange = (date, event, position, value) => {
    setEditedRota(prevRota => {
      const newRota = { ...prevRota };
      if (!newRota[`${date} ${event}`]) {
        newRota[`${date} ${event}`] = {};
      }
      newRota[`${date} ${event}`][position] = value;
      return newRota;
    });
  };

  const exportToCSV = () => {
    if (!rota) return;

    const csvContent = [
      ['Date', 'Event', ...positions].join(','),
      ...groupedAndSortedRota.map(({ date, event, assignments }) => 
        [
          formatDate(date),
          event,
          ...positions.map(position => assignments[position] || '-')
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${rotaName || 'rota'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleAssignmentCounts = () => {
    setShowAssignmentCounts(!showAssignmentCounts);
  };

  if (!rota) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading rota data...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-10 bg-gray-50 rounded-lg shadow-md">
      <h1 className="text-4xl font-bold text-gray-800 text-center mb-8">
        {rotaName || 'View Rota'}
      </h1>
      
      {/* Rota Table */}
      <div className="overflow-x-auto mb-8 bg-white rounded-lg shadow-sm">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="p-4 text-left border-b border-gray-200 bg-gray-100 font-semibold text-gray-700 uppercase text-sm tracking-wide">
                Date
              </th>
              <th className="p-4 text-left border-b border-gray-200 bg-gray-100 font-semibold text-gray-700 uppercase text-sm tracking-wide">
                Event
              </th>
              {positions.map(position => (
                <th key={position} className="p-4 text-left border-b border-gray-200 bg-gray-100 font-semibold text-gray-700 uppercase text-sm tracking-wide">
                  {position}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupedAndSortedRota.map(({ date, event, assignments }, index, array) => (
              <tr key={`${date}-${event}`} className={index === 0 || date !== array[index - 1].date ? 'border-t-4 border-gray-100' : ''}>
                {(index === 0 || date !== array[index - 1].date) && (
                  <td rowSpan={array.filter(item => item.date === date).length} className="p-4 font-bold text-lg bg-gray-50 align-top border-b border-gray-200">
                    {formatDate(date)}
                  </td>
                )}
                <td className="p-4 font-semibold text-primary-600 border-b border-gray-200">
                  {event}
                </td>
                {positions.map(position => (
                  <td key={`${date}-${event}-${position}`} className="p-4 border-b border-gray-200">
                    {isEditing ? (
                      <select
                        value={editedRota[`${date} ${event}`]?.[position] || '-'}
                        onChange={(e) => handleAssignmentChange(date, event, position, e.target.value)}
                        className="form-select text-sm"
                      >
                        <option value="-">-</option>
                        {getQualifiedMembers(position).map(member => (
                          <option key={member.id} value={member.name}>{member.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-gray-800">{assignments[position] || '-'}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Action Controls */}
      <div className="flex flex-wrap gap-4 justify-center">
        {isEditing ? (
          <>
            <button 
              onClick={handleSave} 
              className="btn-primary px-6 py-2"
            >
              Save Changes
            </button>
            <button 
              onClick={handleCancel} 
              className="btn-danger px-6 py-2"
            >
              Cancel
            </button>
            <button 
              onClick={checkForConflicts} 
              className="px-6 py-2 bg-orange-500 text-white rounded font-semibold hover:bg-orange-600 transition-colors"
            >
              Check Conflicts
            </button>
          </>
        ) : (
          <button 
            onClick={handleEdit} 
            className="btn-primary px-6 py-2"
          >
            Edit Rota
          </button>
        )}
        <button 
          onClick={exportToCSV} 
          className="px-6 py-2 bg-cyan-500 text-white rounded font-semibold hover:bg-cyan-600 transition-colors"
        >
          Export to CSV
        </button>
        <button 
          onClick={toggleAssignmentCounts} 
          className="btn-secondary px-6 py-2"
        >
          Count Assignments
        </button>
      </div>

      {/* Assignment Counts Modal */}
      {showAssignmentCounts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Assignment Counts</h2>
            <ul className="space-y-2 mb-6">
              {Object.entries(assignmentCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => (
                  <li key={name} className="flex justify-between py-1">
                    <span className="text-gray-700">{name}:</span>
                    <span className="font-semibold text-gray-900">{count}</span>
                  </li>
                ))}
            </ul>
            <button 
              onClick={toggleAssignmentCounts}
              className="btn-primary w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Conflicts Modal */}
      {showConflicts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Conflicts Found</h2>
            {conflicts.length === 0 ? (
              <p className="text-green-600 mb-6">No conflicts found in the rota.</p>
            ) : (
              <ul className="space-y-2 mb-6">
                {conflicts.map((conflict, index) => (
                  <li key={index} className="text-red-600">
                    <span className="font-semibold">{conflict.event}:</span> {conflict.names.join(', ')}
                  </li>
                ))}
              </ul>
            )}
            <button 
              onClick={() => setShowConflicts(false)}
              className="btn-primary w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewRotaPage;